/**
 * Macro targets derived from what the user actually told us.
 *
 * The dashboard used to hardcode a 28/36/32 split and a 25 g fiber target for
 * every user, ignoring the UserNutritionPreferences the backend stores. That
 * model has proteinTargetG, fiberTargetG and dietStyle, but no carb or fat
 * target — so the split comes from dietStyle and the explicit gram targets
 * override it where they exist.
 *
 * Anything that cannot be derived is null. The caller renders an empty state;
 * it must not invent a number.
 */

const KCAL_PER_G_PROTEIN = 4;
const KCAL_PER_G_CARB = 4;
const KCAL_PER_G_FAT = 9;

// Keyed by UserNutritionPreferences.dietStyle. `balanced` is the fallback.
export const DIET_STYLE_SPLITS = {
  balanced: { protein: 0.25, carbs: 0.45, fats: 0.3 },
  high_protein: { protein: 0.35, carbs: 0.35, fats: 0.3 },
  low_carb: { protein: 0.3, carbs: 0.25, fats: 0.45 },
  mediterranean: { protein: 0.2, carbs: 0.45, fats: 0.35 },
  plant_forward: { protein: 0.2, carbs: 0.5, fats: 0.3 },
  keto: { protein: 0.25, carbs: 0.05, fats: 0.7 },
  other: { protein: 0.25, carbs: 0.45, fats: 0.3 },
};

// Zero is treated as unset, not as an explicit zero-gram target: a stored 0
// is indistinguishable from "never filled in", and honoring it as a real
// target would make it a denominator later (progress-bar percentages,
// adherence thresholds), turning it into NaN or Infinity. Consistent with
// goalDefaults.js's positiveOr for the same reason.
const positiveNumber = (value) => (
  typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : null
);

export function resolveMacroTargets({ calorieGoal, nutritionPreferences } = {}) {
  const goal = positiveNumber(calorieGoal);
  const split = DIET_STYLE_SPLITS[nutritionPreferences?.dietStyle] || DIET_STYLE_SPLITS.balanced;

  const explicitProtein = positiveNumber(nutritionPreferences?.proteinTargetG);
  const explicitFiber = positiveNumber(nutritionPreferences?.fiberTargetG);

  return {
    protein: explicitProtein ?? (goal ? Math.round((goal * split.protein) / KCAL_PER_G_PROTEIN) : null),
    carbs: goal ? Math.round((goal * split.carbs) / KCAL_PER_G_CARB) : null,
    fats: goal ? Math.round((goal * split.fats) / KCAL_PER_G_FAT) : null,
    // No default: there is no stored fiber target to derive one from, and 25 g
    // was a guess presented as the user's own goal.
    fiber: explicitFiber,
  };
}
